import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import pt from '@/locales/pt.json'
import { loadJapaneseFont } from '@/lib/cjk-font'
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  URL_PREFIX_LANGUAGES,
  isLanguage,
  isUrlPrefixLanguage,
  type Language,
  type UrlPrefixLanguage,
} from '@/lib/languages'

export const STORAGE_KEY = 'agrobio.lang'

/**
 * A lista de idiomas mora em `@/lib/languages` (fonte única — ver o cabeçalho
 * daquele módulo). Aqui ela é apenas re-exportada para não quebrar os
 * consumidores que já importam de `@/lib/i18n`.
 */
export {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  URL_PREFIX_LANGUAGES,
  isLanguage,
  isUrlPrefixLanguage,
}
export type { Language, UrlPrefixLanguage }

export type TranslateParams = Record<string, string | number>

type LocaleBundle = Record<string, string>

const ptResources = pt as LocaleBundle

/**
 * Primeiro segmento das rotas que NÃO são território de idioma: elas ficam
 * fora do wrapper `/:lang?` em `App.tsx` (portanto sem `LocaleGate`) e não têm
 * equivalente prefixado. Um path desses não declara idioma nenhum — ali quem
 * decide continua sendo localStorage/navigator.
 */
const LANGUAGE_NEUTRAL_SEGMENTS = new Set(['login', 'app', 'trace'])

/** Segmentos do path, sem vazios. `/ja/forum/1` -> ['ja','forum','1']. */
function segmentsOf(pathname: string): string[] {
  return (pathname || '/').split('/').filter(Boolean)
}

/** `true` para `/login`, `/app/...`, `/trace/...` — fora da árvore de idioma. */
export function isLanguageNeutralPath(pathname: string): boolean {
  const first = segmentsOf(pathname)[0]
  return first !== undefined && LANGUAGE_NEUTRAL_SEGMENTS.has(first)
}

/** `true` só para a raiz do site (`/`) — a única URL pública sem idioma declarado. */
export function isSiteRoot(pathname: string): boolean {
  return segmentsOf(pathname).length === 0
}

/**
 * Extrai o idioma do primeiro segmento da URL.
 * Só `ja`/`en`/`es` são prefixos; `/pt` não conta (PT é a raiz).
 */
function langFromPathname(pathname: string): Language | null {
  const segment = segmentsOf(pathname)[0]
  return isUrlPrefixLanguage(segment) ? segment : null
}

/**
 * Idioma DECLARADO pela URL atual, com PT como valor da ausência de prefixo.
 *
 * É esta função que `useLocalizedPath`/`LocalizedLink` usam para prefixar links
 * internos. Ela lê a URL — e não o contexto de idioma — de propósito: a URL é a
 * autoridade única (D2/D3), e assim um link continua correto mesmo fora do
 * `LanguageProvider` (ex.: `NotFoundPage`, renderizada pelo `LocaleGate` antes
 * do layout público).
 */
export function languageFromPathname(pathname: string): Language {
  return langFromPathname(pathname) ?? DEFAULT_LANGUAGE
}

/**
 * Remove o prefixo de idioma (`/ja`, `/en`, `/es`) do path, devolvendo sempre
 * um path absoluto. `/ja/diretorio` -> `/diretorio`; `/en` -> `/`.
 * Função pura — sem router, sem window.
 */
export function stripLanguagePrefix(pathname: string): string {
  const segments = segmentsOf(pathname)
  if (isUrlPrefixLanguage(segments[0])) segments.shift()
  return segments.length > 0 ? `/${segments.join('/')}` : '/'
}

/**
 * Constrói a URL equivalente no idioma alvo, preservando a página atual, a
 * querystring e o hash. PT vive na raiz (sem prefixo); `ja`/`en`/`es` ganham
 * prefixo. Função pura — sem router, sem window.
 *
 * O hash entra aqui porque ele é parte da posição do usuário na página
 * (`/ja/diretorio?q=bio#resultados`): trocar de idioma não pode jogá-lo de
 * volta ao topo do documento.
 */
export function buildLocalizedPath(
  pathname: string,
  lang: Language,
  search = '',
  hash = '',
): string {
  const bare = stripLanguagePrefix(pathname)
  const prefix = isUrlPrefixLanguage(lang) ? `/${lang}` : ''
  const path = bare === '/' ? prefix || '/' : `${prefix}${bare}`
  return `${path}${search ?? ''}${hash ?? ''}`
}

function langFromNavigator(navigatorLang: string | undefined | null): Language | null {
  if (!navigatorLang) return null
  const lower = navigatorLang.toLowerCase()
  if (lower.startsWith('ja')) return 'ja'
  if (lower.startsWith('en')) return 'en'
  if (lower.startsWith('es')) return 'es'
  if (lower.startsWith('pt')) return 'pt'
  return null
}

/**
 * Precedência: URL > localStorage > navigator > pt (D3 do PLANO-I18N).
 *
 * "URL" não é só o prefixo `/ja|/en|/es`: a AUSÊNCIA de prefixo numa rota
 * pública também é informação de URL — `/diretorio` é a versão portuguesa da
 * página, por definição. Por isso ela fixa `pt` aqui, em vez de deixar
 * localStorage/navigator vencerem e o `LocaleGate` corrigir depois (o que
 * produzia flash de idioma errado ao abrir `/diretorio` com `agrobio.lang=en`).
 *
 * Duas exceções, ambas deliberadas:
 * - rotas fora da árvore de idioma (`/login`, `/app/*`, `/trace/*`): não são
 *   território de idioma nem têm equivalente prefixado — ali a preferência do
 *   usuário continua valendo;
 * - a raiz `/`: é o x-default, a única URL pública que não declara idioma
 *   nenhum — é dela que sai a escolha de primeira visita por `navigator`.
 *
 * Função pura — sem I/O, sem i18next, sem window.
 */
export function resolveInitialLanguage(
  pathname: string,
  stored: string | null,
  navigatorLang: string | undefined | null,
): Language {
  const urlLang = langFromPathname(pathname)
  if (urlLang) return urlLang
  if (!isSiteRoot(pathname) && !isLanguageNeutralPath(pathname)) return 'pt'
  if (isLanguage(stored)) return stored
  const navLang = langFromNavigator(navigatorLang)
  if (navLang) return navLang
  return 'pt'
}

export function persistLanguage(lang: Language): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, lang)
}

/** Chunks lazy de en/es/ja. PT já vem no bundle inicial. */
const localeModules = import.meta.glob<{ default: LocaleBundle }>('../locales/{en,es,ja}.json')

/**
 * Carga do DICIONÁRIO em voo/concluída, por idioma. Duas trocas rápidas para o
 * mesmo idioma (ou `LocaleGate` e `LanguageContext` disparando juntos)
 * compartilham a MESMA carga em vez de duplicar a requisição do chunk.
 *
 * A memoização cobre só o dicionário — a fonte CJK é pedida a cada aplicação
 * de `ja` (ver `ensureLocaleLoaded`), porque quem memoiza a fonte é o próprio
 * `loadJapaneseFont`. Misturar as duas coisas aqui faria uma sessão que já
 * carregou `ja.json` pular o pedido da fonte.
 */
const localeBundleLoads = new Map<Language, Promise<void>>()

function findLocaleLoader(lang: Language): (() => Promise<{ default: LocaleBundle }>) | undefined {
  const entry = Object.entries(localeModules).find(([path]) => path.endsWith(`/${lang}.json`))
  return entry?.[1]
}

async function loadLocaleBundle(lang: Language): Promise<void> {
  const loader = findLocaleLoader(lang)
  // Sem chunk para este idioma (pt vem no bundle inicial; um JSON ausente cai
  // no `fallbackLng`): nada a carregar.
  if (!loader) return
  const mod = await loader()
  i18n.addResourceBundle(lang, 'translation', mod.default, true, true)
}

export async function ensureLocaleLoaded(lang: Language): Promise<void> {
  // A fonte CJK viaja junto do dicionário japonês (D5): nenhuma sessão em
  // pt/en/es dispara este import. `loadJapaneseFont` é memoizado, então
  // repetir a chamada não gera nova requisição.
  const fontLoading = lang === 'ja' ? loadJapaneseFont() : null

  let bundleLoading = localeBundleLoads.get(lang)
  if (!bundleLoading) {
    bundleLoading = loadLocaleBundle(lang).catch((error: unknown) => {
      // Falha não fica memoizada: uma nova tentativa pode dar certo (rede).
      localeBundleLoads.delete(lang)
      throw error
    })
    localeBundleLoads.set(lang, bundleLoading)
  }

  await Promise.all([bundleLoading, fontLoading])
}

/**
 * Geração da última requisição de troca de idioma. "Última requisição vence":
 * trocar `en -> es` rápido pode resolver o bundle EN DEPOIS do ES; sem este
 * token, a conclusão obsoleta chamaria `changeLanguage('en')` e deixaria a UI
 * em inglês numa URL `/es`.
 */
let applyGeneration = 0

/**
 * Aplica o idioma: carrega o bundle (se houver) e troca no i18next.
 * Se o JSON do idioma ainda não existir, o idioma ativo passa a ser o alvo
 * mesmo assim e o i18next resolve os textos pelo `fallbackLng: 'pt'` — a URL
 * continua sendo a autoridade única (D3).
 *
 * Conclusões obsoletas são descartadas: só a ÚLTIMA chamada aplica.
 */
export async function applyLanguage(lang: Language): Promise<void> {
  const generation = ++applyGeneration
  await ensureLocaleLoaded(lang)
  if (generation !== applyGeneration) return
  if (i18n.language !== lang) {
    await i18n.changeLanguage(lang)
  }
}

export function translate(key: string, params?: TranslateParams): string {
  const options: TranslateParams | undefined = params ? { ...params } : undefined
  if (options && typeof options.count === 'number' && options.n === undefined) {
    options.n = options.count
  }
  const result = i18n.t(key, options)
  return typeof result === 'string' ? result : key
}

export const i18nReady = i18n.use(initReactI18next).init({
  lng: 'pt',
  fallbackLng: 'pt',
  supportedLngs: [...SUPPORTED_LANGUAGES],
  resources: {
    pt: { translation: ptResources },
  },
  interpolation: {
    escapeValue: false,
    prefix: '{',
    suffix: '}',
  },
  keySeparator: false,
  nsSeparator: false,
  pluralSeparator: '_',
  returnNull: false,
  initAsync: false,
  react: {
    useSuspense: false,
  },
})

/**
 * Resolve o idioma inicial e carrega o JSON (se não for pt) ANTES do primeiro
 * paint — elimina flash de idioma errado.
 */
export async function bootstrapI18n(): Promise<void> {
  await i18nReady
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
  const navigatorLang = typeof navigator !== 'undefined' ? navigator.language : undefined
  const lang = resolveInitialLanguage(pathname, stored, navigatorLang)
  // Mesma autoridade do LocaleGate: o idioma resolvido vale mesmo quando o
  // bundle ainda não existe (fallback pt do i18next cobre os textos).
  await applyLanguage(lang)
}

export default i18n
